import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useBoard } from "@/hooks/useBoard";
import { Card, Label, MonoMeta } from "@/design/primitives";
import { cn } from "@/lib/utils";

// Kit's real capacity, measured by Kit (kitstatus-beat, every 10 min):
//   cloud = ollama-cloud proxy slots in flight (5 accounts, 29 total)
//   local = Mac's loaded Ollama model lanes (4)
//   queued2h = scheduled thinking jobs firing in the next 2 hours (scripted monitors excluded)
// One bar + a plain-English verdict. Green <60%, amber 60–89%, red ≥90%.
// Jeff 10/30 (Desk 2 build): compactBar = just the bar, the percentage, the
// slot line and live sessions — no lanes grid, no next-2h block. totalOverride
// rescales the display (he set the working number at 50 slots).

const verdict = (pct, queued, live) => {
  if (!live) return "Measuring…";
  if (pct >= 90) return "At capacity — finish current work before handing Kit more.";
  if (pct >= 60) return "Getting busy — room for small jobs only.";
  if (queued >= 4) return "Queue filling — next 2 hours are packed; big jobs land after.";
  return "Low — plenty of room for more work.";
};

const Bar = ({ pct }) => {
  const tone = pct >= 90 ? "bg-os-red" : pct >= 60 ? "bg-os-amber" : "bg-os-green";
  return (
    <div data-testid="kit-capacity-bar" className="h-2 w-full overflow-hidden rounded-full bg-os-border/60">
      <div className={cn("h-full rounded-full transition-all", tone)} style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
    </div>
  );
};

export default function KitCapacity({ compact, compactBar, totalOverride }) {
  const board = useBoard("kitstatus");
  const cap = useMemo(() => (board.items || []).find((r) => r.slug === "kit-capacity"), [board.items]);

  const p = cap?.payload || {};
  const q = p.queued2h || [];
  const total = totalOverride ?? p.total ?? 0;
  const used = p.used ?? 0;
  const live = board.live && total > 0;
  const pct = totalOverride ? Math.round((used / total) * 100) : p.pct || 0;

  if (!cap) {
    return compact ? null : (
      <Card as="section" className="px-5 py-4" data-testid="kit-capacity-empty">
        <p className="text-[0.8125rem] text-os-ink-3">Capacity feed reaching Kit… (measured every 10 minutes)</p>
      </Card>
    );
  }

  if (compactBar) {
    return (
      <Card as="section" className="px-5 py-4" data-testid="kit-capacity">
        <div className="flex items-baseline justify-between gap-3">
          <Label as="h2">Kit's capacity</Label>
          <MonoMeta as="span" data-testid="kit-capacity-pct" className={cn(pct >= 90 ? "text-os-red" : pct >= 60 ? "text-os-amber" : "text-os-green")}>
            {pct}% · {used}/{total} slots
          </MonoMeta>
        </div>
        <div className="mt-2"><Bar pct={pct} /></div>
        <p data-testid="kit-capacity-verdict" className="mt-2 text-[0.8125rem] text-os-ink-2">
          {verdict(pct, q.length, live)}
          {typeof p.liveSessions === "number" ? ` · ${p.liveSessions} live session${p.liveSessions === 1 ? "" : "s"}` : ""}
        </p>
      </Card>
    );
  }

  return (
    <Card as="section" className="px-5 py-4" data-testid="kit-capacity">
      <div className="flex items-baseline justify-between gap-3">
        <Label as="h2">Kit's capacity</Label>
        <MonoMeta as="span" data-testid="kit-capacity-pct" className={cn(pct >= 90 ? "text-os-red" : pct >= 60 ? "text-os-amber" : "text-os-green")}>
          {pct}% · {used}/{total} slots
        </MonoMeta>
      </div>
      <div className="mt-2"><Bar pct={pct} /></div>
      <p data-testid="kit-capacity-verdict" className="mt-2 text-[0.8125rem] text-os-ink-2">{verdict(pct, q.length, live)}</p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div>
          <p className="font-mono text-[0.6875rem] text-os-ink-3">CLOUD SLOTS</p>
          <p className="text-[0.875rem] font-medium tabular-nums">{p.cloud?.used ?? 0}/{p.cloud?.total ?? 0}{p.cloud?.cooling ? ` · ${p.cloud.cooling} cooling` : ""}</p>
        </div>
        <div>
          <p className="font-mono text-[0.6875rem] text-os-ink-3">LOCAL LANES</p>
          <p className="text-[0.875rem] font-medium tabular-nums">{p.local?.used ?? 0}/{p.local?.total ?? 0}</p>
        </div>
        <div>
          <p className="font-mono text-[0.6875rem] text-os-ink-3">LIVE SESSIONS</p>
          <p className="text-[0.875rem] font-medium tabular-nums">{p.liveSessions ?? 0}</p>
        </div>
      </div>
      <div className="mt-3 border-t border-os-border pt-3">
        <p className="font-mono text-[0.6875rem] text-os-ink-3">NEXT 2 HOURS — {p.queued2hN ?? q.length} SCHEDULED</p>
        {q.length === 0 ? (
          <p className="mt-1 text-[0.8125rem] text-os-ink-3">Nothing scheduled — the window is open.</p>
        ) : (
          <ul className="mt-1 flex flex-col gap-0.5">
            {q.slice(0, 5).map((j, i) => (
              <li key={i} className="text-[0.8125rem] text-os-ink-2">
                <span className="font-mono text-[0.6875rem] text-os-ink-3">{j.at}</span>{" — "}{j.name}
              </li>
            ))}
            {q.length > 5 && <li className="text-[0.75rem] text-os-ink-3">+ {q.length - 5} more in the window</li>}
          </ul>
        )}
      </div>
      <MonoMeta as="p" className="mt-2">
        measured {p.updated ? new Date(p.updated).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""} · every 10 min ·{" "}
        <Link
          to="/status#status-accounts"
          data-testid="kit-capacity-link"
          className="underline underline-offset-2 hover:text-os-ink"
        >
          the full picture →
        </Link>
      </MonoMeta>
    </Card>
  );
}